// Onda 4 (fase 2) — Página pública do Drive de um contrato Hydrodata.
// URL: /publico/drive/{hash} ou /#/drive/{hash}
// Cliente externo (sem login) lista, baixa e — se o token permitir — envia
// arquivos na pasta "Upload do Cliente". Resolve via operacao.drive_token.
// 2026-06-15: migrado pra Antd (consistência c/ Drive interno), árvore de
// subpastas recursiva e barra de progresso percentual no upload.
import { useEffect, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  App as AntApp, Table, Button, Progress, Spin, Empty, Modal, Input, Form,
  Typography, Tag, Space, Flex,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined, CloudUploadOutlined, UserOutlined,
  CloudDownloadOutlined, FileZipOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';
import { fmtDataBr as _fmtDataBr } from '../utils/dataFmt';

const { Text, Title, Paragraph } = Typography;

interface DriveFolder {
  id: string; parent_id: string | null;
  nome: string; tipo: string; ordem: number;
}
interface DriveFile {
  id: string; folder_id: string; nome: string;
  tipo_arquivo: string | null; tamanho_bytes: number | null;
  versao: number; descricao: string | null;
  origem: string | null; criado_em: string;
}
interface DriveData {
  contrato: { numero: string; titulo: string };
  cliente: { razao_social: string };
  permissao_upload: boolean;
  folders: DriveFolder[];
  files: DriveFile[];
}

const TIPO_ICONE: Record<string, string> = {
  comercial: '📋', arts: '🪪', relatorios: '📄',
  pecas_graficas: '🎨', upload_cliente: '📥', usuario: '📁',
};

function iconeArquivo(tipo: string | null): string {
  switch (tipo) {
    case 'pdf':     return '📄';
    case 'image':   return '🖼️';
    case 'word':    return '📝';
    case 'sheet':   return '📊';
    case 'slide':   return '📊';
    case 'kml': case 'kmz': return '🗺️';
    case 'cad':     return '📐';
    case 'archive': return '🗜️';
    default:        return '📎';
  }
}

function fmtTamanho(b: number | null): string {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const fmtData = _fmtDataBr;

export default function PaginaPublicaDrive({ hash }: { hash: string }) {
  return <AntApp><PaginaPublicaDriveInner hash={hash} /></AntApp>;
}

function PaginaPublicaDriveInner({ hash }: { hash: string }) {
  const { message, modal } = AntApp.useApp();
  const [data, setData] = useState<DriveData | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [pastaAtiva, setPastaAtiva] = useState<string | null>(null);
  const [baixandoLote, setBaixandoLote] = useState<{ atual: number; total: number; fase: string } | null>(null);
  const [colapsadas, setColapsadas] = useState<Set<string>>(new Set());
  const [enviando, setEnviando] = useState<{ atual: number; total: number; nome: string; pct: number } | null>(null);
  const [dragSobre, setDragSobre] = useState(false);
  const [remetente, setRemetente] = useState<{ nome: string; email: string }>(() => {
    try {
      return JSON.parse(localStorage.getItem('drive-publico:remetente') || '{}');
    } catch { return { nome: '', email: '' }; }
  });
  const [pedeRemetente, setPedeRemetente] = useState(false);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [hash]);

  async function carregar() {
    setLoading(true); setErro('');
    try {
      const r = await axios.get(`${API_URL}/publico/proposta/${hash}`);
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (!d?.contrato || !d?.folders) {
        setErro('Link inválido ou acesso revogado.');
        return;
      }
      setData(d);
      // Default: pasta Comercial
      if (!pastaAtiva && d.folders?.length) {
        const com = d.folders.find((f: DriveFolder) => f.tipo === 'comercial');
        setPastaAtiva(com?.id || d.folders[0].id);
      }
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message || 'Falha ao carregar');
    } finally { setLoading(false); }
  }

  async function baixarArquivo(f: DriveFile) {
    // iOS Safari bloqueia window.open() depois de await — abre síncrono aqui
    // pra preservar a "user activation" que o iOS exige pra popups.
    const w = window.open('', '_blank');
    if (!w) { message.warning('Permita pop-ups deste site pra baixar arquivos.'); return; }
    try {
      const r = await axios.post(`${API_URL}/publico/proposta/${hash}`,
        { action: 'drive_baixar', documento_id: f.id,
          remetente: remetente.nome ? `${remetente.nome} <${remetente.email}>` : null });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.url) { w.location.href = d.url; }
      else { w.close(); message.error('Não foi possível gerar o link de download.'); }
    } catch (e: any) {
      w.close();
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Conjunto de IDs de uma pasta + todas as descendentes (pra contar/dimensionar).
  function pastasDescendentes(folderId: string): Set<string> {
    const fs = data?.folders || [];
    const set = new Set([folderId]);
    let mudou = true;
    while (mudou) {
      mudou = false;
      for (const f of fs) {
        if (f.parent_id && set.has(f.parent_id) && !set.has(f.id)) { set.add(f.id); mudou = true; }
      }
    }
    return set;
  }
  // Arquivos no escopo: uma pasta (+ subpastas) ou o drive inteiro (sem folderId).
  function arquivosDoEscopo(folderId?: string): DriveFile[] {
    const fl = data?.files || [];
    if (!folderId) return fl.filter(f => f.folder_id);
    const ids = pastasDescendentes(folderId);
    return fl.filter(f => ids.has(f.folder_id));
  }

  // Baixa em lote (pasta+subpastas ou tudo): pega as URLs assinadas numa chamada,
  // junta os arquivos num ZIP no navegador (preservando a árvore de pastas).
  async function baixarLote(folderId?: string) {
    const arqs = arquivosDoEscopo(folderId);
    if (arqs.length === 0) { message.info('Não há arquivos pra baixar aqui.'); return; }
    const totalBytes = arqs.reduce((s, f) => s + (f.tamanho_bytes || 0), 0);

    const prosseguir = async () => {
      setBaixandoLote({ atual: 0, total: 0, fase: 'Preparando…' });
      try {
        const r = await axios.post(`${API_URL}/publico/proposta/${hash}`,
          { action: 'drive_baixar_lote',
            folder_id: folderId || undefined,
            remetente: remetente.nome ? `${remetente.nome} <${remetente.email}>` : null });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        const lista: { id: string; caminho: string; url: string }[] = d.arquivos || [];
        const zipNome = (d.zip_nome || 'drive').replace(/[\\/:*?"<>|]/g, '_');
        if (!lista.length) { message.info('Nada pra baixar.'); setBaixandoLote(null); return; }

        const zip = new JSZip();
        let ok = 0, falhas = 0;
        for (let i = 0; i < lista.length; i++) {
          setBaixandoLote({ atual: i + 1, total: lista.length, fase: 'Baixando arquivos' });
          try {
            const resp = await fetch(lista[i].url, { mode: 'cors', cache: 'no-store' });
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            zip.file(lista[i].caminho, await resp.blob());
            ok++;
          } catch { falhas++; }
        }
        if (ok === 0) {
          message.error('Não consegui baixar os arquivos. Se você atualizou o sistema recentemente, dê Ctrl+Shift+R e tente de novo.');
          return;
        }
        setBaixandoLote({ atual: lista.length, total: lista.length, fase: 'Compactando…' });
        const blob = await zip.generateAsync({ type: 'blob' });
        saveAs(blob, `${zipNome}.zip`);
        if (falhas > 0) message.warning(`${ok} arquivo(s) no zip; ${falhas} não baixaram.`);
        else message.success(`${ok} arquivo(s) baixados.`);
      } catch (e: any) {
        message.error('Erro: ' + (e.response?.data?.error || e.message));
      } finally {
        setBaixandoLote(null);
      }
    };

    // ZIP é montado no navegador → avisa antes de baixar coisas muito grandes.
    if (totalBytes > 1.5 * 1024 * 1024 * 1024) {
      modal.confirm({
        title: 'Download grande',
        content: `São ${arqs.length} arquivos (~${fmtTamanho(totalBytes)}). O ZIP é montado aqui no navegador e pode demorar e consumir bastante memória. Quer continuar?`,
        okText: 'Baixar mesmo assim', cancelText: 'Cancelar',
        onOk: prosseguir,
      });
    } else {
      await prosseguir();
    }
  }

  function salvarRemetente(nome: string, email: string) {
    setRemetente({ nome, email });
    try { localStorage.setItem('drive-publico:remetente', JSON.stringify({ nome, email })); } catch {}
    setPedeRemetente(false);
  }

  async function uploadArquivos(arquivos: FileList | File[], _pastaId: string) {
    if (!data?.permissao_upload) return;
    if (!remetente.email) {
      setPedeRemetente(true);
      return;
    }
    const lista = Array.from(arquivos);
    if (lista.length === 0) return;
    setEnviando({ atual: 0, total: lista.length, nome: '', pct: 0 });
    try {
      for (let i = 0; i < lista.length; i++) {
        const file = lista[i];
        setEnviando({ atual: i + 1, total: lista.length, nome: file.name, pct: 0 });
        if (file.size > 200 * 1024 * 1024) {
          message.warning(`"${file.name}" excede 200MB. Pulando.`);
          continue;
        }
        // 1) presigned PUT
        const r1 = await axios.post(`${API_URL}/publico/proposta/${hash}`,
          { action: 'drive_upload_url',
            filename: file.name,
            content_type: file.type || 'application/octet-stream' });
        const d1 = r1.data.body ? JSON.parse(r1.data.body) : r1.data;
        if (!d1.upload_url || !d1.s3_key || !d1.folder_id) {
          message.error(`Erro ao obter URL de upload pra "${file.name}"`);
          continue;
        }
        // 2) PUT direto S3 (com progresso real de bytes)
        await axios.put(d1.upload_url, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setEnviando(s => s ? { ...s, pct } : s);
          },
        });
        // 3) confirma upload
        await axios.post(`${API_URL}/publico/proposta/${hash}`,
          { action: 'drive_confirmar_upload',
            s3_key: d1.s3_key, folder_id: d1.folder_id, nome: file.name,
            tamanho_bytes: file.size,
            remetente_nome: remetente.nome, remetente_email: remetente.email });
      }
      message.success('Envio concluído.');
    } catch (e: any) {
      message.error('Erro no upload: ' + (e.response?.data?.error || e.message));
    } finally {
      setEnviando(null);
      carregar();
    }
  }

  function toggleColapso(id: string) {
    setColapsadas(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  if (loading) {
    return (
      <Layout>
        <Flex justify="center" style={{ padding: 60 }}><Spin tip="Carregando…" size="large" /></Flex>
      </Layout>
    );
  }

  if (erro || !data) {
    return (
      <Layout>
        <div style={{ padding: 50, textAlign: 'center' }}>
          <div style={{ fontSize: 50, marginBottom: 14 }}>🔒</div>
          <Title level={3} style={{ color: '#020D7F', marginBottom: 8 }}>Link inválido</Title>
          <Text type="secondary">{erro || 'Acesso revogado ou link expirado.'}</Text>
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 18 }}>
            Solicite um novo link à equipe Hydrodata.
          </Paragraph>
        </div>
      </Layout>
    );
  }

  const folders = data.folders;
  const filhosDe = (pid: string | null) =>
    folders.filter(f => (f.parent_id || null) === pid)
      .sort((a, b) => (a.ordem - b.ordem) || a.nome.localeCompare(b.nome));

  const pasta = folders.find(f => f.id === pastaAtiva);
  const arquivosPasta = data.files.filter(f => f.folder_id === pastaAtiva);
  const podeUploadAqui = data.permissao_upload && pasta?.tipo === 'upload_cliente';

  // Linha de pasta na árvore — recursiva (N níveis), com expand/collapse.
  function renderNo(f: DriveFolder, nivel: number) {
    const filhos = filhosDe(f.id);
    const temFilhos = filhos.length > 0;
    const aberta = !colapsadas.has(f.id);
    const ativa = pastaAtiva === f.id;
    const arqs = data!.files.filter(x => x.folder_id === f.id).length;
    return (
      <div key={f.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4,
          padding: '5px 8px', paddingLeft: 8 + nivel * 14, cursor: 'pointer',
          background: ativa ? '#E7ECFD' : 'transparent', borderRadius: 4,
          fontSize: nivel === 0 ? 12 : 11,
          fontWeight: ativa ? 600 : (nivel === 0 ? 500 : 400),
          color: ativa ? '#020D7F' : (nivel === 0 ? '#333' : '#555') }}>
          {temFilhos ? (
            <span onClick={(e) => { e.stopPropagation(); toggleColapso(f.id); }}
              style={{ width: 14, textAlign: 'center', color: '#999', userSelect: 'none' }}>
              {aberta ? '▾' : '▸'}
            </span>
          ) : <span style={{ width: 14 }} />}
          <span onClick={() => setPastaAtiva(f.id)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, overflow: 'hidden' }}>
            <span style={{ fontSize: 14 }}>{nivel === 0 ? (TIPO_ICONE[f.tipo] || '📁') : '📁'}</span>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap' }}>{f.nome}</span>
            {arqs > 0 && <Text type="secondary" style={{ fontSize: 10 }}>{arqs}</Text>}
          </span>
        </div>
        {temFilhos && aberta && filhos.map(c => renderNo(c, nivel + 1))}
      </div>
    );
  }

  const colunas: ColumnsType<DriveFile> = [
    { title: 'Arquivo', key: 'nome',
      render: (_, f) => (
        <div>
          <span style={{ fontSize: 14, marginRight: 6 }}>{iconeArquivo(f.tipo_arquivo)}</span>
          <Text strong style={{ color: '#222' }}>{f.nome}</Text>
          {f.versao > 1 && <Tag color="blue" style={{ fontSize: 9, marginLeft: 6 }}>v{f.versao}</Tag>}
          {f.descricao && (
            <div><Text type="secondary" style={{ fontSize: 10 }}>{f.descricao}</Text></div>
          )}
        </div>
      ),
    },
    { title: 'Tamanho', dataIndex: 'tamanho_bytes', key: 'tam', width: 90, align: 'right',
      render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{fmtTamanho(v)}</Text> },
    { title: 'Disponível em', dataIndex: 'criado_em', key: 'em', width: 120,
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtData(v)}</Text> },
    { title: '', key: 'acoes', width: 110, align: 'center',
      render: (_, f) => (
        <Button type="primary" size="small" icon={<DownloadOutlined />}
          style={{ background: '#0930D0', borderColor: '#0930D0' }}
          onClick={() => baixarArquivo(f)}>
          Baixar
        </Button>
      ),
    },
  ];

  return (
    <Layout>
      {/* Cabeçalho de marca */}
      <div style={{ background: 'linear-gradient(135deg, #020D7F 0%, #0930D0 100%)',
        color: '#fff', padding: '24px 30px', borderRadius: 8, marginBottom: 18 }}>
        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4, letterSpacing: 0.5 }}>
          DRIVE DO CONTRATO
        </div>
        <h1 style={{ margin: 0, fontSize: 22 }}>{data.contrato.titulo}</h1>
        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85, display: 'flex',
          gap: 16, flexWrap: 'wrap' }}>
          <span><strong>Nº</strong> {data.contrato.numero}</span>
          <span><strong>Cliente</strong> {data.cliente.razao_social || '—'}</span>
        </div>
      </div>

      {/* Identificação do remetente */}
      <Flex align="center" justify="space-between" wrap="wrap" gap={8}
        style={{ padding: '10px 14px', background: '#f4f6fb', borderRadius: 5, marginBottom: 14 }}>
        <Text style={{ fontSize: 11, color: '#555' }}>
          {remetente.nome ? (
            <>👤 Identificado como <strong>{remetente.nome}</strong>
              {remetente.email && <span style={{ color: '#888' }}> ({remetente.email})</span>}
            </>
          ) : (
            <>👤 Não identificado — clique pra se identificar (recomendado pra rastreio)</>
          )}
        </Text>
        <Button size="small" icon={<UserOutlined />}
          style={{ borderColor: '#0930D0', color: '#0930D0' }}
          onClick={() => setPedeRemetente(true)}>
          {remetente.nome ? 'Alterar' : 'Identificar'}
        </Button>
      </Flex>

      {/* Ação de download em lote do drive inteiro */}
      {data.files.length > 0 && (
        <Flex justify="flex-end" style={{ marginBottom: 10 }}>
          <Button icon={<CloudDownloadOutlined />} loading={!!baixandoLote}
            onClick={() => baixarLote()}
            style={{ borderColor: '#0930D0', color: '#0930D0' }}>
            Baixar tudo (zip)
          </Button>
        </Flex>
      )}

      {/* Layout principal: árvore + arquivos */}
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 14 }}>
        <div style={{ background: '#fff', border: '0.5px solid #e0e0e0',
          borderRadius: 5, padding: 8 }}>
          {filhosDe(null).map(f => renderNo(f, 0))}
        </div>

        <div>
          {pasta && (
            <>
              <Flex justify="space-between" align="flex-start" gap={8} wrap="wrap" style={{ marginBottom: 12 }}>
                <div>
                  <Title level={5} style={{ margin: 0, color: '#020D7F' }}>
                    {TIPO_ICONE[pasta.tipo] || '📁'} {pasta.nome}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {pasta.tipo === 'upload_cliente'
                      ? data.permissao_upload
                        ? 'Você pode enviar arquivos aqui pra equipe Hydrodata.'
                        : 'Esta pasta está bloqueada pra envio. Solicite à equipe.'
                      : `${arquivosPasta.length} arquivo${arquivosPasta.length === 1 ? '' : 's'} disponível${arquivosPasta.length === 1 ? '' : 'eis'} pra download.`}
                  </Text>
                </div>
                {arquivosDoEscopo(pasta.id).length > 0 && (
                  <Button size="small" icon={<FileZipOutlined />} loading={!!baixandoLote}
                    onClick={() => baixarLote(pasta.id)}
                    style={{ borderColor: '#0930D0', color: '#0930D0' }}>
                    Baixar pasta ({arquivosDoEscopo(pasta.id).length})
                  </Button>
                )}
              </Flex>

              {/* Dropzone só na pasta upload_cliente */}
              {podeUploadAqui && (
                <div onDragOver={e => { e.preventDefault(); setDragSobre(true); }}
                  onDragLeave={() => setDragSobre(false)}
                  onDrop={e => {
                    e.preventDefault(); setDragSobre(false);
                    if (e.dataTransfer.files?.length) uploadArquivos(e.dataTransfer.files, pasta.id);
                  }}
                  style={{ border: dragSobre ? '2px dashed #0930D0' : '1px dashed #ccc',
                    borderRadius: 6, padding: 18, textAlign: 'center', marginBottom: 12,
                    background: dragSobre ? '#E7ECFD' : '#FAFAFA' }}>
                  {enviando ? (
                    <div style={{ textAlign: 'left' }}>
                      <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                        <Text strong style={{ color: '#0930D0', fontSize: 12 }}>
                          Enviando {enviando.atual} de {enviando.total}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, maxWidth: '60%',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {enviando.nome}
                        </Text>
                      </Flex>
                      <Progress percent={enviando.pct} size="small" status="active"
                        strokeColor="#0930D0" />
                    </div>
                  ) : (
                    <Space direction="vertical" size={8}>
                      <Text style={{ color: '#666', fontSize: 12 }}>
                        <CloudUploadOutlined /> Arraste arquivos aqui pra enviar pra Hydrodata
                      </Text>
                      <input type="file" multiple
                        onChange={e => {
                          if (e.target.files) uploadArquivos(e.target.files, pasta.id);
                          e.target.value = '';
                        }}
                        style={{ fontSize: 11 }} />
                    </Space>
                  )}
                </div>
              )}

              {arquivosPasta.length === 0 ? (
                <Empty description="Pasta vazia." />
              ) : (
                <Table<DriveFile> size="small" rowKey="id" columns={colunas}
                  dataSource={arquivosPasta} pagination={false} />
              )}
            </>
          )}
        </div>
      </div>

      {baixandoLote && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000,
          background: '#fff', border: '1px solid #d8dcec', borderRadius: 8,
          padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 240 }}>
          <Text strong style={{ color: '#0930D0', fontSize: 12 }}>{baixandoLote.fase}</Text>
          {baixandoLote.total > 0 && (
            <>
              <Progress percent={Math.round((baixandoLote.atual / baixandoLote.total) * 100)}
                size="small" status="active" strokeColor="#0930D0" />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {baixandoLote.atual} de {baixandoLote.total} arquivo(s)
              </Text>
            </>
          )}
        </div>
      )}

      {pedeRemetente && (
        <ModalRemetente atual={remetente}
          onSalvar={salvarRemetente} onCancelar={() => setPedeRemetente(false)} />
      )}
    </Layout>
  );
}

function ModalRemetente({ atual, onSalvar, onCancelar }: {
  atual: { nome: string; email: string };
  onSalvar: (nome: string, email: string) => void;
  onCancelar: () => void;
}) {
  const [nome, setNome] = useState(atual.nome || '');
  const [email, setEmail] = useState(atual.email || '');
  return (
    <Modal open onCancel={onCancelar} width={420} destroyOnClose
      title={<Title level={5} style={{ margin: 0, color: '#020D7F' }}>Identificação</Title>}
      okText="Salvar" okButtonProps={{ disabled: !email.trim() }}
      onOk={() => onSalvar(nome.trim(), email.trim())}>
      <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 14 }}>
        Pra rastreio de quem baixou/enviou, informe seu nome e e-mail.
        (salvos somente neste navegador, não obrigatórios pra download)
      </Paragraph>
      <Form layout="vertical">
        <Form.Item label="Nome" style={{ marginBottom: 12 }}>
          <Input value={nome} onChange={e => setNome(e.target.value)} autoFocus />
        </Form.Item>
        <Form.Item label="E-mail" style={{ marginBottom: 0 }}>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh',
      background: 'linear-gradient(135deg, #f4f6fb 0%, #E7ECFD 100%)',
      padding: '30px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {children}
        <div style={{ textAlign: 'center', fontSize: 10, color: '#888',
          marginTop: 30, paddingTop: 16, borderTop: '0.5px solid #d8dcec' }}>
          Hydrodata Engenharia · Drive interno do contrato · Acesso por link
        </div>
      </div>
    </div>
  );
}
